import React, { useState } from 'react';
import type { Card } from "../../gameboardTypes.tsx";
import StaticCard from '../../staticCard.tsx';
import { PlayingCard } from '../../../../paths/games/cardComponent.tsx';
import useWindowDimensions from '../../../../util/windowUtils.ts';
import useCardDimensions from '../../../../util/cardUtils.ts';
import DraggableCard from './draggableCard.tsx';
import { cardHasEffectType } from '../../../../util/cardEffects/effectProcessing.tsx';
import { createText } from '../../../../util/cardEffects/cardText.ts';

interface HandProps {
    cards?: Card[] | number,
    playerFocus?: { count: number; temp_count: number; },
    playCard?: Function
}

// i had to redo basically this entire file at one point, so I hope you like comments
export default function Hand({ cards, playerFocus, playCard }: HandProps) {
    const handCardScale = .75;
    const { width } = useWindowDimensions();
    const { cardWidth, cardHeight } = useCardDimensions(1);
    const { height } = useWindowDimensions();
    const [hovered, setHovered] = useState<number | null>(null);

    const playCardLine = height * (3 / 5);

    const baseCardOverlap = .5;
    const maxCardCountBeforeSquish = 7;
    const fanParabolicScale = 0.18;
    const baseX = width * (23 / 40); // yeah, yeah, it's hyperspecific, this get's it pretty centered though. this should be the center of the hand.
    const baseY = (cardHeight * handCardScale) * 13 / 20; // yeah, also this
    const maxHandWidth = (maxCardCountBeforeSquish * cardWidth * handCardScale) - ((maxCardCountBeforeSquish - 2) * cardWidth * handCardScale * baseCardOverlap);

    let handElements = [];

    // to future me or whoever has to edit hand fanning, I'm so sorry. 
    // there's literally derivatives involved in this... 
    // an explanation of how positioning works though: 

    // For Y position;
    // we (metaphorically) draw a parabola, scaled by fanParabolicScale, 
    // and index cards in hand with 0 being the center card
    // then, I offset the card position to that x-pos (the index) on the parabola (with scaling)
    // lastly there's a base-Y of a portion of the card height to move the fan off screen a little

    // For X position; 
    // start with another base offset from left of screen, to put between
    // the player info card and pass turn / concede buttons
    // next, if there are less than 1+ maxCardCountBeforeSquish cards in hand, offset them
    // by baseCardOverlap * cardWidth. if there are more than that, fit them into the same
    // amount of space by shrinking the overlap.

    // For rotation; 
    // here's where derivatives come in : ) 
    // use the derivative of the parabola for Y positioning to find the slope of the 
    // tangent line of said parabola at the card's index position. sx^2 -> 2sx
    // convert that slope to an angle with Math.atan(slope)
    // convert the radians to degrees to work with css (180 / PI) * rad

    if (typeof cards === 'number') {

        const count = cards;
        const cardOverlap = Math.min(baseCardOverlap, ((maxCardCountBeforeSquish * baseCardOverlap) / count));
        const attemptedHandWidth = count * (cardWidth * handCardScale) - ((count - 1) * (cardWidth * handCardScale) * cardOverlap);
        const handWidth = Math.min(maxHandWidth, attemptedHandWidth);
        const deltaX = (cardWidth * handCardScale) * cardOverlap;

        for (let i = 0; i < count; i++) {

            const index = (i - ((count - 1) / 2)) / count * 2;
            const yPos = fanParabolicScale * (index * index) * (cardHeight * handCardScale);
            const slope = 2 * fanParabolicScale * index;
            const x = baseX + (deltaX * i) - (handWidth / 2);
            const y = -baseY - yPos;
            const rot = (180 / Math.PI) * Math.atan(slope);

            handElements.push(
                <StaticCard
                    key={`static-${i}-${width}-${count}-${x}-${y}`}
                    rotation={-rot}
                    X={x}
                    Y={y}
                >
                    <PlayingCard
                        faceDown={true}
                        scale={handCardScale}
                    />
                </StaticCard>
            );
        }
    } else if (cards) {

        const count = cards?.length ?? 0;
        const cardOverlap = Math.min(baseCardOverlap, ((maxCardCountBeforeSquish * baseCardOverlap) / ((count - 1))));
        const attemptedHandWidth = count * (cardWidth * handCardScale) - ((count - 1) * (cardWidth * handCardScale) * cardOverlap);
        const handWidth = Math.min(maxHandWidth, attemptedHandWidth);
        const deltaX = (cardWidth * handCardScale) * cardOverlap;

        for (let i = 0; i < count; i++) {
            const index = (i - ((count - 1) / 2)) / count * 2;
            const yPos = fanParabolicScale * (index * index) * (cardHeight * handCardScale);
            const slope = 2 * fanParabolicScale * index;
            const x = baseX + (deltaX * i) - (handWidth / 2);
            const y = height - ((cardHeight * handCardScale) - baseY) + yPos;
            const rot = (180 / Math.PI) * Math.atan(slope);

            handElements.push(
                <DraggableCard
                    key={`${cards[i].id}-${width}-${count}-${x}-${y}`}
                    initialRotation={rot}
                    initialX={x}
                    initialY={y}
                    dropCallabck={(currentPos: { x: number, y: number }) => {
                        if (playCard && currentPos.y < playCardLine) {
                            playCard(cards[i].id);
                        }
                    }}
                    hoverCallback={(val = i) => {
                        setHovered(val);
                    }}
                >
                    <PlayingCard
                        scale={handCardScale}
                        name={cards[i].name}
                        school={cards[i].school}
                        cost={cards[i].cost}
                        castThreshold={cards[i].castThreshold}
                        ritual={cardHasEffectType("ritual", cards[i])}
                        highlightOnFullFocus={true}

                        focus={playerFocus?.count}
                        tempFocus={playerFocus?.temp_count}

                        play={createText(cards[i].playAbility)}
                        sustain={createText(cards[i].sustainAbility)}
                        cast={createText(cards[i].castAbility)}
                        passive={createText(cards[i].passiveAbility)}
                    />
                </DraggableCard>
            );

            // if a card is hovered, display it so people can read it
            if (i == hovered) {
                const hoveredCardScale = 1;
                handElements.push(
                    <StaticCard
                        key={`hovered-${i}-${width}-${count}-${x}-${y}`}
                        rotation={0}
                        X={width - (cardWidth * hoveredCardScale)}
                        Y={(height - (cardHeight * hoveredCardScale)) / 2}
                    >
                        <PlayingCard
                            scale={hoveredCardScale}
                            name={cards[i].name}
                            school={cards[i].school}
                            cost={cards[i].cost}
                            castThreshold={cards[i].castThreshold}
                            ritual={cardHasEffectType("ritual", cards[i])}
                            highlightOnFullFocus={true}

                            focus={playerFocus?.count}
                            tempFocus={playerFocus?.temp_count}

                            play={createText(cards[i].playAbility)}
                            sustain={createText(cards[i].sustainAbility)}
                            cast={createText(cards[i].castAbility)}
                            passive={createText(cards[i].passiveAbility)}
                        />
                    </StaticCard>
                )
            }
        }
    }

    // return the aggergate of one of the two options
    return (<div>
        {/* this div shows where cards are cast in */}
        {/* <div style={{ border: "1px solid red", height: `${playCardLine}px`, position: "absolute", top: 0, width: "100%" }}></div> */}
        {handElements}
    </div>)
}